import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  FloppyDisk,
  File,
  FolderSimple,
  FilePdf,
  FileDoc,
  Video,
  MusicNote,
  Exam,
  CloudArrowUp,
  Trash,
  TextT,
  FileArrowUp,
  Gear,
  X,
  FileText,
  Image as ImageIcon,
  Link as LinkIcon,
  Eye,
  CaretLeft,
  CaretRight,
  Info,
  ListChecks
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import QuizBuilder, { DEFAULT_QUIZ_SETTINGS } from '../../components/quiz/QuizBuilder';
import BankLocationPicker, { EMPTY_BANK_LOCATION, bankLocationToExam, bankLocationFromExam } from '../../components/quiz/BankLocationPicker';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Document, Page, pdfjs } from 'react-pdf';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// Helper function to convert video URLs to embed format
const getEmbedUrl = (url) => {
  if (!url) return null;
  
  // YouTube regular URL: https://www.youtube.com/watch?v=VIDEO_ID
  const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
  if (youtubeMatch) {
    return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
  }
  
  // Vimeo: https://vimeo.com/VIDEO_ID
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }
  
  // Cloudflare Stream embed URL
  if (url.includes('cloudflarestream.com') || url.includes('videodelivery.net')) {
    // Already an embed URL or Stream URL
    if (url.includes('/iframe/') || url.includes('iframe.cloudflarestream.com')) {
      return url;
    }
    // Convert to iframe URL
    const streamIdMatch = url.match(/(?:cloudflarestream\.com|videodelivery\.net)\/([a-zA-Z0-9]+)/);
    if (streamIdMatch) {
      return `https://iframe.cloudflarestream.com/${streamIdMatch[1]}`;
    }
  }
  
  // If it's already an embed URL or direct video file, return as-is
  return url;
};

// Chapter → Topic → Subtopic. A subtopic can nest further subtopics without limit. Exams are a
// subtopic content type (stored as node_type 'exam' for compatibility with reports / progress);
// legacy 'file' subtopics are edited as ordinary lesson content.
const NODE_TYPES = [
  { value: 'section', label: 'Chapter', icon: FolderSimple },
  { value: 'subsection', label: 'Topic', icon: FolderSimple },
  { value: 'content', label: 'Subtopic (lesson content)', icon: File },
];
const NODE_TYPE_LABEL = { section: 'Chapter', subsection: 'Topic', content: 'Subtopic' };

const CONTENT_TYPES = [
  { value: 'text', label: 'Rich Text / HTML' },
  { value: 'pdf', label: 'PDF Document' },
  { value: 'ppt', label: 'PowerPoint' },
  { value: 'docx', label: 'Word Document' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'text_audio', label: 'Text + Audio' },
  { value: 'external_link', label: 'External Link' },
  { value: 'quiz', label: 'Quiz (build it here)' },
  { value: 'exam', label: 'Exam (pick an existing exam)' },
];

const MAX_UPLOAD_MB = 100; // keep in step with the backend limit on /upload

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'font': [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'list': 'ordered' }, { 'list': 'bullet' }],
    [{ 'indent': '-1' }, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ],
};

const quillFormats = [
  'header', 'font',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link', 'image', 'video'
];

const getFileIcon = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  if (['pdf'].includes(ext)) return FilePdf;
  if (['doc', 'docx'].includes(ext)) return FileDoc;
  if (['ppt', 'pptx'].includes(ext)) return FileText;
  if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return Video;
  if (['mp3', 'wav', 'ogg'].includes(ext)) return MusicNote;
  if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) return ImageIcon;
  return File;
};

const isPdfFile = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return ext === 'pdf';
};

// PDF Viewer Component with Tools
function PdfViewer({ filePath, token }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfData, setPdfData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [fitMode, setFitMode] = useState('width');
  const containerRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = useState(600);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - 40);
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    const fetchPdf = async () => {
      try {
        setLoading(true);
        // Import dynamically to avoid circular dependency
        const { getBackendUrl } = await import('../../config/api');
        const backendUrl = getBackendUrl();
        const actualPath = filePath.includes('|') ? filePath.split('|')[0] : filePath;
        
        let url;
        if (actualPath.startsWith('/api/files/')) {
          url = `${backendUrl}${actualPath}`;
        } else {
          url = `${backendUrl}/api/files/serve?path=${encodeURIComponent(actualPath)}`;
        }
        
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (!response.ok) throw new Error('Failed to load PDF');
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        setPdfData(blobUrl);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (filePath && token) fetchPdf();

    return () => {
      if (pdfData) URL.revokeObjectURL(pdfData);
    };
  }, [filePath, token]);

  const zoomIn = () => { setFitMode('custom'); setScale(s => Math.min(s + 0.25, 3.0)); };
  const zoomOut = () => { setFitMode('custom'); setScale(s => Math.max(s - 0.25, 0.5)); };
  const fitToWidth = () => { setFitMode('width'); setScale(1.0); };
  const fitToPage = () => { setFitMode('page'); setScale(1.0); };

  const getPageWidth = () => {
    if (fitMode === 'width') return containerWidth;
    if (fitMode === 'page') return Math.min(containerWidth, 550);
    return 550 * scale;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-muted rounded-lg">
        <div className="animate-pulse">Loading PDF...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64 bg-red-50 rounded-lg text-red-600">
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-gray-100" ref={containerRef}>
      {/* PDF Toolbar */}
      <div className="flex items-center justify-between p-2 bg-gray-700 text-white gap-2 flex-wrap">
        {/* Page Navigation */}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white hover:bg-gray-600" disabled={pageNumber <= 1} onClick={() => setPageNumber(p => p - 1)} title="Previous">
            <CaretLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs px-2 min-w-[70px] text-center">{pageNumber} / {numPages || '?'}</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white hover:bg-gray-600" disabled={pageNumber >= numPages} onClick={() => setPageNumber(p => p + 1)} title="Next">
            <CaretRight className="w-4 h-4" />
          </Button>
        </div>

        {/* Zoom Controls */}
        <div className="flex items-center gap-1 border-l border-gray-500 pl-2">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white hover:bg-gray-600" onClick={zoomOut} title="Zoom out">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" strokeWidth="2"/><path strokeWidth="2" d="M21 21l-4-4M8 11h6"/></svg>
          </Button>
          <span className="text-xs px-2 min-w-[55px] text-center font-mono bg-gray-600 rounded">
            {fitMode === 'width' ? 'W' : fitMode === 'page' ? 'P' : `${Math.round(scale * 100)}%`}
          </span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white hover:bg-gray-600" onClick={zoomIn} title="Zoom in">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" strokeWidth="2"/><path strokeWidth="2" d="M21 21l-4-4M11 8v6M8 11h6"/></svg>
          </Button>
        </div>

        {/* Fit Controls */}
        <div className="flex items-center gap-1 border-l border-gray-500 pl-2">
          <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs hover:bg-gray-600 ${fitMode === 'width' ? 'bg-gray-600' : ''}`} onClick={fitToWidth} title="Fit width">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeWidth="2" d="M4 12h16M4 12l3-3M4 12l3 3M20 12l-3-3M20 12l-3 3"/></svg>
          </Button>
          <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs hover:bg-gray-600 ${fitMode === 'page' ? 'bg-gray-600' : ''}`} onClick={fitToPage} title="Fit page">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><rect x="5" y="3" width="14" height="18" rx="2" strokeWidth="2"/></svg>
          </Button>
        </div>
      </div>

      {/* PDF Content */}
      <div className="overflow-auto bg-gray-400 flex justify-center cursor-grab active:cursor-grabbing" style={{ maxHeight: '450px', minHeight: '250px' }}>
        <div className="p-4">
          <Document file={pdfData} onLoadSuccess={({ numPages }) => setNumPages(numPages)} loading={<div className="animate-pulse p-8 bg-white rounded">Loading...</div>}>
            <Page pageNumber={pageNumber} width={getPageWidth()} renderTextLayer={false} renderAnnotationLayer={false} className="shadow-xl" />
          </Document>
        </div>
      </div>
    </div>
  );
}

export default function NodeEditor() {
  const { courseId, nodeId } = useParams();
  const [searchParams] = useSearchParams();
  const parentId = searchParams.get('parentId');
  const isNew = nodeId === 'new';
  
  const { api, token } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [course, setCourse] = useState(null);
  useTabLabel(course?.title ? `Structure: ${course.title}` : null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [previewFile, setPreviewFile] = useState(null);
  const [exams, setExams] = useState([]);
  
  // File naming dialog state
  const [namingDialog, setNamingDialog] = useState({ open: false, files: [], currentIndex: 0, names: [] });
  
  // Video source tracking: 'url' for external URL, 'upload' for uploaded file
  const [videoSource, setVideoSource] = useState('url');
  // Quiz content type: questions + rules built right here (saved as an exam linked to this node)
  const [quiz, setQuiz] = useState({ questions: [], settings: { ...DEFAULT_QUIZ_SETTINGS } });
  // Push to the Question bank (quiz / exam content): where it appears in the bank tree
  const [bank, setBank] = useState({ enabled: false, ...EMPTY_BANK_LOCATION });
  const [formData, setFormData] = useState({
    title: '',
    node_type: 'content',
    content_type: 'text',
    content_text: '',
    overview: '',
    external_url: '',
    file_url: '',
    is_required: false,
    duration_minutes: 0,
    parent_id: parentId || null,
    order_index: 0,
    exam_id: null
  });

  const fetchNode = useCallback(async () => {
    try {
      const courseRes = await api.get(`/courses/${courseId}`);
      setCourse(courseRes.data);
      
      // Fetch all exams (exams are reusable across courses)
      const examsRes = await api.get(`/exams`);
      setExams(examsRes.data);
      
      const nodesRes = await api.get(`/courses/${courseId}/nodes?flat=true`); // flat: parents may be nested at any depth
      const allNodes = nodesRes.data;
      
      if (!isNew) {
        const nodeRes = await api.get(`/courses/${courseId}/nodes/${nodeId}`);
        const node = nodeRes.data;
        const isExam = node.node_type === 'exam' || node.content_type === 'exam' || node.content_type === 'quiz';
        const isQuiz = isExam && node.content_type === 'quiz';
        if (isExam && node.exam_id) {
          try {
            const ex = (await api.get(`/exams/${node.exam_id}`)).data;
            const cats = (await api.get('/categories')).data || [];
            setBank({ enabled: !!ex.in_question_bank, ...bankLocationFromExam(ex, cats) });
            if (isQuiz) {
              const qs = (await api.get(`/question-bank?exam_id=${node.exam_id}&limit=2000`)).data || [];
              const order = new Map((ex.questions || []).map((l, i) => [l.question_id, i]));
              qs.sort((a, b) => (order.get(a.id) ?? 999) - (order.get(b.id) ?? 999));
              setQuiz({ questions: qs, settings: { time_limit_minutes: ex.settings?.time_limit_minutes ?? '', passing_score: ex.settings?.passing_score ?? 60, max_attempts: ex.settings?.max_attempts ?? 1, shuffle_questions: !!ex.settings?.shuffle_questions } });
            }
          } catch { /* exam may have been deleted — the editor still opens */ }
        }
        setFormData({
          title: node.title || '',
          node_type: ['section', 'subsection'].includes(node.node_type) ? node.node_type : 'content',
          content_type: isQuiz ? 'quiz' : isExam ? 'exam' : (node.content_type || 'text'),
          content_text: node.content_text || '',
          overview: node.overview || '',
          external_url: node.external_url || '',
          file_url: node.file_url || '',
          is_required: node.is_required || false,
          duration_minutes: node.duration_minutes || 0,
          parent_id: node.parent_id,
          order_index: node.order_index || 0,
          exam_id: node.exam_id || null
        });
        // Detect video source from existing URL
        if (node.content_type === 'video' && node.external_url) {
          setVideoSource(node.external_url.startsWith('/api/files/') ? 'upload' : 'url');
        }
        // Parse existing files - format: path|name,path|name
        if (node.file_url) {
          const files = node.file_url.split(',').filter(f => f.trim()).map(f => {
            const parts = f.split('|');
            return { path: parts[0], name: parts[1] || parts[0].split('/').pop() };
          });
          setUploadedFiles(files);
        }
      } else {
        const siblingNodes = allNodes.filter(n => n.parent_id === parentId);
        const maxOrder = siblingNodes.length > 0 
          ? Math.max(...siblingNodes.map(s => s.order_index || 0)) + 1 
          : 0;
        const parent = parentId ? allNodes.find(n => n.id === parentId) : null;
        try {
          const cats = (await api.get('/categories')).data || [];
          setBank((b) => ({ ...b, ...bankLocationFromExam({ bank_category_id: courseRes.data.category_id, bank_course_id: courseId, bank_node_id: parentId || '' }, cats) }));
        } catch { /* keep defaults */ }
        // sensible default: top level → chapter, under a chapter → topic, under anything else → subtopic
        const defaultType = !parent ? 'section' : parent.node_type === 'section' ? 'subsection' : 'content';
        setFormData(prev => ({ ...prev, order_index: maxOrder, node_type: defaultType }));
      }
    } catch (error) {
      toast.error('Failed to load node data');
      navigate(`/instructor/courses/${courseId}/structure`);
    } finally {
      setLoading(false);
    }
  }, [api, courseId, nodeId, isNew, parentId, navigate]);

  useEffect(() => {
    fetchNode();
  }, [fetchNode]);

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    const isQuiz = formData.node_type === 'content' && formData.content_type === 'quiz';
    const isExamPick = formData.node_type === 'content' && formData.content_type === 'exam';
    if (isQuiz && quiz.questions.length === 0) { toast.error('Add at least one question to the quiz'); return; }
    if (isExamPick && !formData.exam_id) { toast.error('Pick the exam for this subtopic'); return; }
    if ((isQuiz || isExamPick) && bank.enabled && !bank.subject_id) { toast.error('Choose the subject (category) for the question bank'); return; }
    const bankFields = bank.enabled ? bankLocationToExam(bank) : { bank_category_id: '', bank_course_id: '', bank_node_id: '' };

    setSaving(true);
    try {
      let examId = formData.exam_id;
      if (isQuiz) {
        // the quiz is a real exam record linked to this subtopic
        const payload = {
          title: formData.title.trim(), course_id: courseId, assessment_type: 'quiz', is_published: true,
          questions: quiz.questions.map((q, i) => ({ question_id: q.id, order: i })),
          settings: { time_limit_minutes: quiz.settings.time_limit_minutes ? parseInt(quiz.settings.time_limit_minutes) : null, passing_score: parseFloat(quiz.settings.passing_score) || 60, max_attempts: parseInt(quiz.settings.max_attempts) || 1, shuffle_questions: !!quiz.settings.shuffle_questions, show_results_immediately: true, show_correct_answers: true, show_explanations: true },
          in_question_bank: bank.enabled, ...bankFields,
        };
        if (examId) await api.put(`/exams/${examId}`, payload);
        else examId = (await api.post('/exams', payload)).data.id;
      } else if (isExamPick) {
        await api.put(`/exams/${examId}`, { in_question_bank: bank.enabled, ...bankFields });
      }

      // Store files as path|name,path|name format
      const fileUrlString = uploadedFiles.map(f => `${f.path}|${f.name}`).join(',');
      const saveData = {
        ...formData,
        // a Quiz / Exam subtopic is stored as node_type 'exam' so progress, reports and the player keep working
        node_type: isQuiz || isExamPick ? 'exam' : formData.node_type,
        exam_id: isQuiz || isExamPick ? examId : null,
        file_url: fileUrlString
      };
      
      if (isNew) {
        await api.post(`/courses/${courseId}/nodes`, saveData);
        toast.success('Node created successfully');
      } else {
        await api.put(`/courses/${courseId}/nodes/${nodeId}`, saveData);
        toast.success('Node updated successfully');
      }
      navigate(`/instructor/courses/${courseId}/structure`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save node');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${formData.title}" and all its children?`)) return;
    try {
      await api.delete(`/courses/${courseId}/nodes/${nodeId}`);
      toast.success('Node deleted');
      navigate(`/instructor/courses/${courseId}/structure`);
    } catch (error) {
      toast.error('Failed to delete node');
    }
  };

  // When files are selected, open naming dialog
  const handleFileSelect = (e) => {
    let files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const tooBig = files.filter((f) => f.size > MAX_UPLOAD_MB * 1024 * 1024);
    if (tooBig.length) {
      toast.error(`${tooBig.map((f) => f.name).join(', ')} — larger than the ${MAX_UPLOAD_MB} MB limit`);
      files = files.filter((f) => f.size <= MAX_UPLOAD_MB * 1024 * 1024);
      if (!files.length) { e.target.value = ''; return; }
    }
    
    // Initialize names with original file names (without extension for display)
    const initialNames = files.map(f => {
      const nameParts = f.name.split('.');
      nameParts.pop(); // Remove extension
      return nameParts.join('.');
    });
    
    setNamingDialog({
      open: true,
      files: files,
      currentIndex: 0,
      names: initialNames
    });
    
    e.target.value = '';
  };

  const updateFileName = (index, newName) => {
    setNamingDialog(prev => ({
      ...prev,
      names: prev.names.map((n, i) => i === index ? newName : n)
    }));
  };

  const handleUploadWithNames = async () => {
    const { files, names } = namingDialog;
    setNamingDialog({ open: false, files: [], currentIndex: 0, names: [] });
    
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const customName = names[i] || file.name.split('.').slice(0, -1).join('.');
        const ext = file.name.split('.').pop();
        const displayName = `${customName}.${ext}`;
        
        const uploadData = new FormData();
        uploadData.append('file', file);
        
        const res = await api.post('/upload', uploadData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Store the file ID from the path (/api/files/{id})
        const fileId = res.data.path.split('/').pop();
        setUploadedFiles(prev => [...prev, { path: res.data.path, name: displayName, fileId: fileId }]);
      }
      toast.success(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      // The server says WHY (usually "File storage unavailable — configure Cloudflare R2
      // in Super Admin settings"). Swallowing it left people guessing.
      toast.error(error.response?.data?.detail || 'Failed to upload file(s)');
    } finally {
      setUploading(false);
    }
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    if (previewFile === index) setPreviewFile(null);
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const showContentFields = formData.node_type === 'content';
  const isFolder = formData.node_type === 'section' || formData.node_type === 'subsection';

  // Extract file ID from path
  const getFileIdFromPath = (path) => {
    if (path.startsWith('/api/files/')) {
      return path.split('/').pop();
    }
    return null;
  };

  const backToStructure = () => navigate(`/instructor/courses/${courseId}/structure`);

  if (loading) {
    return (
      <DashboardLayout>
        <RecordPage title="Node" loading={loading} onBack={backToStructure} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="node-editor"
        title={isNew ? (formData.title || 'New node') : formData.title}
        subtitle={course?.title}
        onBack={backToStructure}
        onRefresh={isNew ? undefined : fetchNode}
       
        primary={[
          { key: 'save', label: saving ? 'Saving...' : (isNew ? 'Create node' : 'Save'), icon: FloppyDisk, onClick: handleSave, disabled: saving, testid: 'save-node-btn' },
          { key: 'cancel', label: 'Cancel', variant: 'outline', onClick: backToStructure },
        ]}
        secondary={[
          { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: handleDelete, hidden: isNew, testid: 'delete-node-btn' },
        ]}
      >
            <Tabs defaultValue="content" className="space-y-4">
              {/* Content Tab with Settings on Right */}
              <TabsContent value="content" className="space-y-4">
                <div className="grid lg:grid-cols-4 gap-4 items-start">
                  {/* Main Content Area - 3 columns */}
                  <div className="lg:col-span-3 space-y-4">
                    {/* Node Information */}
                    <FormSection title="Node Information" icon={File} bodyClassName="p-3 space-y-3">
                        {/* Title · Node type · Content type — one line on a wide screen */}
                        <div className={`grid gap-3 md:grid-cols-2 ${showContentFields ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
                          <div className="space-y-1.5 md:col-span-2">
                            <Label htmlFor="title" className="text-xs font-medium">Title *</Label>
                            <Input
                              id="title"
                              value={formData.title}
                              onChange={(e) => updateField('title', e.target.value)}
                              placeholder="Enter node title"
                             
                              data-testid="node-title-input"
                            />
                          </div>
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium">Node Type</Label>
                            <Select
                              value={formData.node_type}
                              onValueChange={(v) => updateField('node_type', v)}
                            >
                              <SelectTrigger data-testid="node-type-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {NODE_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    <div className="flex items-center gap-2">
                                      <type.icon className="w-4 h-4" />
                                      {type.label}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {showContentFields && (
                            <div className="space-y-1.5">
                              <Label className="text-xs font-medium">Content Type</Label>
                              <Select
                                value={formData.content_type}
                                onValueChange={(v) => updateField('content_type', v)}
                              >
                                <SelectTrigger data-testid="content-type-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {CONTENT_TYPES.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                    </FormSection>

                    {/* Rich Text Editor for text content */}
                    {showContentFields && ['text', 'text_audio'].includes(formData.content_type) && (
                      <FormSection title="Content Editor" icon={TextT} bodyClassName="p-3">
                          <div className="border rounded-md overflow-hidden" data-testid="rich-text-editor">
                            <ReactQuill
                              theme="snow"
                              value={formData.content_text}
                              onChange={(value) => updateField('content_text', value)}
                              modules={quillModules}
                              formats={quillFormats}
                              style={{ minHeight: '280px' }}
                              placeholder="Start writing your content here..."
                            />
                          </div>
                      </FormSection>
                    )}

                    {/* URL input for video/external link */}
                    {showContentFields && (formData.content_type === 'video' || formData.content_type === 'external_link') && (
                      <FormSection
                        title={formData.content_type === 'video' ? 'Video Source' : 'External URL'}
                        icon={formData.content_type === 'video' ? Video : LinkIcon}
                        bodyClassName="p-3 space-y-3"
                      >
                          {formData.content_type === 'video' ? (
                            <Tabs value={videoSource} onValueChange={(tab) => {
                              // Clear data when switching source
                              if (tab === 'url' && formData.external_url?.startsWith('/api/files/')) {
                                updateField('external_url', '');
                              } else if (tab === 'upload' && formData.external_url && !formData.external_url.startsWith('/api/files/')) {
                                updateField('external_url', '');
                              }
                              setVideoSource(tab);
                            }} className="w-full">
                              <TabsList className="grid w-full grid-cols-2">
                                <TabsTrigger value="url" data-testid="video-tab-url">External URL</TabsTrigger>
                                <TabsTrigger value="upload" data-testid="video-tab-upload">Upload Video</TabsTrigger>
                              </TabsList>
                              <TabsContent value="url" className="space-y-4 pt-4">
                                <div className="space-y-1.5">
                                  <Label htmlFor="external_url" className="text-xs font-medium">Video URL</Label>
                                  <Input
                                    id="external_url"
                                    value={videoSource === 'url' ? formData.external_url : ''}
                                    onChange={(e) => {
                                      updateField('external_url', e.target.value);
                                      setVideoSource('url');
                                    }}
                                    placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..."
                                    className="h-9"
                                    data-testid="external-url-input"
                                  />
                                  <p className="field-hint">
                                    Supports: YouTube, Vimeo, Cloudflare Stream, or direct video URLs
                                  </p>
                                </div>
                                {videoSource === 'url' && formData.external_url && (
                                  <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                    {getEmbedUrl(formData.external_url) ? (
                                      <iframe 
                                        src={getEmbedUrl(formData.external_url)} 
                                        className="w-full h-full" 
                                        allowFullScreen 
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        title="Video Preview"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-white">
                                        <p className="text-sm text-center px-4">
                                          Unable to preview this video URL. 
                                          <br />
                                          Supported: YouTube, Vimeo, Cloudflare Stream
                                        </p>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </TabsContent>
                              <TabsContent value="upload" className="space-y-4 pt-4">
                                {/* Show uploaded video if exists */}
                                {videoSource === 'upload' && formData.external_url && formData.external_url.startsWith('/api/files/') ? (
                                  <div className="space-y-4">
                                    <div className="flex items-center justify-between p-3 bg-muted/30 border rounded-lg">
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                                          <Video className="w-4 h-4 text-primary" />
                                        </div>
                                        <div>
                                          <p className="text-sm font-medium">Video uploaded</p>
                                          <p className="field-hint">Ready for playback</p>
                                        </div>
                                      </div>
                                      <Button 
                                        variant="outline" 
                                        size="sm"
                                        onClick={() => {
                                          updateField('external_url', '');
                                        }}
                                        data-testid="remove-uploaded-video"
                                      >
                                        <X className="w-4 h-4 mr-1" />
                                        Remove
                                      </Button>
                                    </div>
                                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                                      <video
                                        src={`${process.env.REACT_APP_BACKEND_URL}${formData.external_url}/stream?token=${encodeURIComponent(token)}`}
                                        className="w-full h-full"
                                        controls
                                        title="Uploaded Video Preview"
                                        data-testid="uploaded-video-preview"
                                      />
                                    </div>
                                  </div>
                                ) : (
                                  <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                                    <input
                                      type="file"
                                      accept="video/mp4,video/webm,video/mov,video/*"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (!file) return;
                                        
                                        if (file.size > 200 * 1024 * 1024) {
                                          toast.error('Video file must be less than 200MB');
                                          return;
                                        }
                                        
                                        setUploading(true);
                                        setUploadProgress(0);
                                        
                                        try {
                                          const formDataUpload = new FormData();
                                          formDataUpload.append('file', file);
                                          
                                          const response = await api.post('/upload/video', formDataUpload, {
                                            headers: { 'Content-Type': 'multipart/form-data' },
                                            onUploadProgress: (progressEvent) => {
                                              const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                              setUploadProgress(progress);
                                            }
                                          });
                                          
                                          const { embed_url, playback_url, stream_id, path } = response.data;
                                          
                                          // Set upload result and mark source as upload
                                          updateField('external_url', embed_url || playback_url || path);
                                          setVideoSource('upload');
                                          
                                          toast.success('Video uploaded successfully!');
                                        } catch (error) {
                                          console.error('Upload error:', error);
                                          toast.error(error.response?.data?.detail || 'Failed to upload video');
                                        } finally {
                                          setUploading(false);
                                          setUploadProgress(0);
                                        }
                                      }}
                                      className="hidden"
                                      id="video-upload-input"
                                      disabled={uploading}
                                    />
                                    <label htmlFor="video-upload-input" className="cursor-pointer">
                                      <CloudArrowUp className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                                      <p className="font-medium mb-1">
                                        {uploading ? `Uploading... ${uploadProgress}%` : 'Click to upload video'}
                                      </p>
                                      <p className="field-hint">
                                        MP4, WebM, MOV up to 200MB
                                      </p>
                                    </label>
                                    {uploading && (
                                      <div className="mt-4 w-full bg-muted rounded-full h-2">
                                        <div 
                                          className="bg-primary h-2 rounded-full transition-all duration-300" 
                                          style={{ width: `${uploadProgress}%` }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </TabsContent>
                            </Tabs>
                          ) : (
                            <div className="space-y-1.5">
                              <Label htmlFor="external_url" className="text-xs font-medium">URL</Label>
                              <Input
                                id="external_url"
                                value={formData.external_url}
                                onChange={(e) => updateField('external_url', e.target.value)}
                                placeholder="https://..."
                               
                                data-testid="external-url-input"
                              />
                            </div>
                          )}
                      </FormSection>
                    )}

                    {/* File Upload for PDF, PPT, DOCX, Audio */}
                    {showContentFields && ['pdf', 'ppt', 'docx', 'audio', 'text_audio'].includes(formData.content_type) && (
                      <FormSection
                        title={`Upload ${formData.content_type === 'pdf' ? 'PDF Document' : formData.content_type === 'ppt' ? 'PowerPoint' : formData.content_type === 'docx' ? 'Word Document' : formData.content_type === 'text_audio' ? 'Audio (one or more files, played above the text)' : 'Audio File'}`}
                        icon={formData.content_type === 'pdf' ? FilePdf : formData.content_type === 'ppt' ? FileText : formData.content_type === 'docx' ? FileDoc : MusicNote}
                        description={['audio', 'text_audio'].includes(formData.content_type) ? `Up to ${MAX_UPLOAD_MB} MB per file — add as many as you need.` : undefined}
                        bodyClassName="p-3 space-y-3"
                      >
                          {/* Show uploaded file if exists */}
                          {uploadedFiles.length > 0 ? (
                            <div className="space-y-3">
                              {uploadedFiles.map((file, index) => {
                                const FileIcon = getFileIcon(file.name);
                                const isPdf = isPdfFile(file.name);
                                return (
                                  <div key={index} className="border rounded-lg overflow-hidden">
                                    <div className="flex items-center justify-between p-3 bg-muted/30">
                                      <div className="flex items-center gap-3 min-w-0">
                                        <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center flex-shrink-0">
                                          <FileIcon className="w-4 h-4 text-primary" />
                                        </div>
                                        <p className="text-sm font-medium truncate">{file.name}</p>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        {isPdf && (
                                          <Button variant="ghost" size="icon" className="h-8 w-8"
                                            onClick={() => setPreviewFile(previewFile === index ? null : index)}
                                            title={previewFile === index ? "Hide preview" : "Show preview"}
                                          >
                                            <Eye className="w-4 h-4" />
                                          </Button>
                                        )}
                                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeFile(index)}>
                                          <X className="w-4 h-4 text-destructive" />
                                        </Button>
                                      </div>
                                    </div>
                                    {isPdf && previewFile === index && (
                                      <div className="border-t p-4">
                                        <PdfViewer filePath={file.path} token={token} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                              {/* Upload more button */}
                              <label className="cursor-pointer">
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={handleFileSelect}
                                  disabled={uploading}
                                  multiple
                                  accept={
                                    formData.content_type === 'pdf' ? '.pdf' :
                                    formData.content_type === 'ppt' ? '.ppt,.pptx' :
                                    formData.content_type === 'docx' ? '.doc,.docx' :
                                    '.mp3,.wav,.ogg,.aac,.flac,.m4a'
                                  }
                                />
                                <Button variant="outline" size="sm" disabled={uploading} asChild>
                                  <span data-testid="upload-more-btn">
                                    <CloudArrowUp className="w-4 h-4 mr-2" />
                                    {uploading ? 'Uploading...' : 'Upload Another File'}
                                  </span>
                                </Button>
                              </label>
                            </div>
                          ) : (
                            <div className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
                              <label className="cursor-pointer block">
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={handleFileSelect}
                                  disabled={uploading}
                                  multiple
                                  accept={
                                    formData.content_type === 'pdf' ? '.pdf' :
                                    formData.content_type === 'ppt' ? '.ppt,.pptx' :
                                    formData.content_type === 'docx' ? '.doc,.docx' :
                                    '.mp3,.wav,.ogg,.aac,.flac,.m4a'
                                  }
                                />
                                <CloudArrowUp className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                                <p className="font-medium mb-1">
                                  {uploading ? 'Uploading...' : 'Click to upload'}
                                </p>
                                <p className="field-hint">
                                  {formData.content_type === 'pdf' && 'PDF files'}
                                  {formData.content_type === 'ppt' && 'PowerPoint (.ppt, .pptx)'}
                                  {formData.content_type === 'docx' && 'Word documents (.doc, .docx)'}
                                  {['audio', 'text_audio'].includes(formData.content_type) && `Audio files (MP3, WAV, OGG, AAC, FLAC) · up to ${MAX_UPLOAD_MB} MB each`}
                                </p>
                              </label>
                            </div>
                          )}
                      </FormSection>
                    )}

                    {/* Lesson overview — shown to learners in the Overview tab beside the video / content */}
                    {showContentFields && formData.content_type !== 'exam' && formData.content_type !== 'quiz' && (
                      <FormSection title="Lesson overview" icon={Info} bodyClassName="p-3">
                        <p className="field-hint mb-2">Shown to learners in the <b>Overview</b> tab next to the {formData.content_type === 'video' ? 'video' : 'content'} — what the lesson covers, key points, what to watch for.</p>
                        <div className="border rounded-md overflow-hidden" data-testid="overview-editor">
                          <ReactQuill
                            theme="snow"
                            value={formData.overview}
                            onChange={(value) => updateField('overview', value)}
                            modules={quillModules}
                            formats={quillFormats}
                            style={{ minHeight: '160px' }}
                            placeholder="Overview of this lesson…"
                          />
                        </div>
                      </FormSection>
                    )}

                    {/* Chapter / Topic details — rich text shown to learners when they open the chapter / topic */}
                    {isFolder && (
                      <FormSection title={`${NODE_TYPE_LABEL[formData.node_type]} details`} icon={TextT} bodyClassName="p-3">
                        <p className="field-hint mb-2">Describe this {NODE_TYPE_LABEL[formData.node_type].toLowerCase()} — learners see it when they open it in the course. {formData.node_type === 'section' ? 'Topics' : 'Subtopics'} are added underneath from the course structure.</p>
                        <div className="border rounded-md overflow-hidden" data-testid="folder-details-editor">
                          <ReactQuill
                            theme="snow"
                            value={formData.content_text}
                            onChange={(value) => updateField('content_text', value)}
                            modules={quillModules}
                            formats={quillFormats}
                            style={{ minHeight: '220px' }}
                            placeholder={`What this ${NODE_TYPE_LABEL[formData.node_type].toLowerCase()} covers…`}
                          />
                        </div>
                      </FormSection>
                    )}

                    {/* Quiz built here */}
                    {showContentFields && formData.content_type === 'quiz' && (
                      <QuizBuilder api={api} courseId={courseId} nodeId={isNew ? null : nodeId} categoryId={bank.enabled ? (bank.category_id || bank.subject_id) : ''} bankNodeId={bank.enabled ? bank.node_id : ''} value={quiz} onChange={setQuiz} />
                    )}

                    {/* Exam info */}
                    {showContentFields && formData.content_type === 'exam' && (
                      <FormSection title="Select Exam" icon={Exam} bodyClassName="p-3 space-y-3">
                          <Select
                            value={formData.exam_id || "none"}
                            onValueChange={(v) => updateField('exam_id', v === "none" ? null : v)}
                          >
                            <SelectTrigger data-testid="exam-select">
                              <SelectValue placeholder="Select an exam" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No exam selected</SelectItem>
                              {exams.map((exam) => (
                                <SelectItem key={exam.id} value={exam.id}>
                                  <div className="flex items-center justify-between w-full gap-4">
                                    <span>{exam.title}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {exam.question_count} Qs
                                    </span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          
                          {formData.exam_id && exams.find(e => e.id === formData.exam_id) && (
                            <div className="p-3 bg-background rounded-lg border">
                              {(() => {
                                const selectedExam = exams.find(e => e.id === formData.exam_id);
                                return (
                                  <div className="space-y-2">
                                    <p className="font-medium">{selectedExam.title}</p>
                                    <div className="flex flex-wrap gap-2 text-xs">
                                      <span className="px-2 py-0.5 bg-muted rounded">{selectedExam.question_count} questions</span>
                                      <span className="px-2 py-0.5 bg-muted rounded">{selectedExam.total_points} points</span>
                                      {selectedExam.settings?.time_limit_minutes && (
                                        <span className="px-2 py-0.5 bg-muted rounded">{selectedExam.settings.time_limit_minutes} min</span>
                                      )}
                                    </div>
                                    {selectedExam.description && (
                                      <p className="field-hint">{selectedExam.description}</p>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                          
                          {!formData.exam_id && (
                            <p className="text-xs text-muted-foreground text-center py-2">
                              Select an exam from the dropdown or{' '}
                              <a href="/instructor/exams/new" className="text-primary underline">create a new exam</a>
                            </p>
                          )}
                      </FormSection>
                    )}
                  </div>

                  {/* Settings Sidebar - 1 column */}
                  <div className="lg:col-span-1 lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto lg:pr-1 space-y-4">
                    <FormSection title="Settings" icon={Gear} bodyClassName="p-3 space-y-3">
                        {/* Required Toggle */}
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-xs font-medium">Required</Label>
                            <p className="field-hint">Must complete</p>
                          </div>
                          <Switch
                            checked={formData.is_required}
                            onCheckedChange={(c) => updateField('is_required', c)}
                            data-testid="required-switch"
                          />
                        </div>

                        <div className="border-t pt-4">
                          <div className="space-y-1.5">
                            <Label htmlFor="duration" className="text-xs font-medium">Duration (min)</Label>
                            <Input
                              id="duration"
                              type="number"
                              min="0"
                              value={formData.duration_minutes}
                              onChange={(e) => updateField('duration_minutes', parseInt(e.target.value) || 0)}
                              placeholder="0"
                             
                              data-testid="duration-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-1.5">
                          <Label htmlFor="order" className="text-xs font-medium">Order Index</Label>
                          <Input
                            id="order"
                            type="number"
                            min="0"
                            value={formData.order_index}
                            onChange={(e) => updateField('order_index', parseInt(e.target.value) || 0)}
                           
                            data-testid="order-input"
                          />
                          <p className="field-hint">Lower = earlier</p>
                        </div>
                    </FormSection>

                    {showContentFields && (formData.content_type === 'quiz' || formData.content_type === 'exam') && (
                      <FormSection title="Question bank" icon={ListChecks} bodyClassName="p-3 space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-xs font-medium">Push to Question bank</Label>
                            <p className="field-hint">List this {formData.content_type} and its questions in the bank tree</p>
                          </div>
                          <Switch checked={bank.enabled} onCheckedChange={(v) => setBank({ ...bank, enabled: v })} data-testid="push-to-bank" />
                        </div>
                        {bank.enabled && (
                          <div className="border-t pt-3">
                            <BankLocationPicker api={api} value={bank} onChange={(loc) => setBank({ ...bank, ...loc })} categoryOnly />
                            <p className="field-hint mt-2">This {formData.content_type} already appears in the bank tree under its own course, chapter and topic. The category decides where its questions are filed. Sub-categories are managed under Question bank → Manage categories.</p>
                          </div>
                        )}
                      </FormSection>
                    )}
                  </div>
                </div>
              </TabsContent>

            </Tabs>

      {/* File Naming Dialog */}
      <Dialog open={namingDialog.open} onOpenChange={(open) => !open && setNamingDialog({ open: false, files: [], currentIndex: 0, names: [] })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Name Your Files</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4 max-h-[400px] overflow-y-auto">
            {namingDialog.files.map((file, index) => {
              const FileIcon = getFileIcon(file.name);
              const ext = file.name.split('.').pop();
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <FileIcon className="w-4 h-4" />
                    <span className="truncate">{file.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      value={namingDialog.names[index] || ''}
                      onChange={(e) => updateFileName(index, e.target.value)}
                      placeholder="Enter display name"
                     
                    />
                    <span className="text-sm text-muted-foreground">.{ext}</span>
                  </div>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNamingDialog({ open: false, files: [], currentIndex: 0, names: [] })}>
              Cancel
            </Button>
            <Button onClick={handleUploadWithNames} disabled={uploading}>
              {uploading ? 'Uploading...' : `Upload ${namingDialog.files.length} File(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </RecordPage>
    </DashboardLayout>
  );
}
